import {PRResultCodes} from 'models/PRResultCodes';
import {View} from 'native-base';
import React, {useEffect, useState} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import {ROUTES} from 'routes';

import {passwordReset} from 'api/profile';
import {useNavigateWithParams} from 'hooks/useNavigateWithParams';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

import {stripTextEncoding} from '../../../utils/utils';
import PRFormField from '../PRFormField';
import {PRSubtitle, PRTitle} from '../PRLabels';
import PRPageTemplate from '../PRPageTemplate';
import {PRWarningBannerProps} from '../PRWarningBanner';

const failedValidationCopy =
  'Please enter an email, mobile number, or Caesars Rewards Number';

const PRInformationEntry = () => {
  const navigate = useNavigateWithParams();
  const location = useLocation();

  const [userInformation, setUserInformation] = useState('');
  const [isValid, setIsValid] = useState<boolean | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('a'));
  const [warning, setWarning] = useState<PRWarningBannerProps | undefined>(
    undefined,
  );

  const {trackAdobeEvent} = useTrackEvent();

  const reason = location?.state?.reason;
  const [APICallFailure, setAPICallFailure] = useState(false);
  const [invalidInfoAPIResponse, setInvalidInfoAPIResponse] = useState(false);

  const submitInfo = async () => {
    setInvalidInfoAPIResponse(false);
    setAPICallFailure(false);
    setLoading(true);
    const formattedUserInformation = formatUserInformation();
    const sanitizedUserInformation = /[a-zA-Z]/.test(formattedUserInformation);
    const response = await passwordReset({
      username: sanitizedUserInformation
        ? formattedUserInformation
        : stripNonNumbers(formattedUserInformation),
    });
    if (response?.data?.header?.resultCode !== PRResultCodes.SUCCESS) {
      setLoading(false);
      if (
        response?.data?.header?.resultCode === PRResultCodes.MISSING_PARAMETER
      ) {
        setInvalidInfoAPIResponse(true);
      } else {
        setAPICallFailure(true);
      }
    } else {
      setLoading(false);
      navigate(ROUTES.passwordResetCode, {
        state: {
          passwordResetToken: response?.data?.body?.authToken,
          userInformation: sanitizedUserInformation
            ? formattedUserInformation
            : stripNonNumbers(formattedUserInformation),
          correlationId: response?.data?.body?.correlationId,
        },
      });
    }
  };

  const handleUserInput = (value: string) => {
    const userInfo = stripTextEncoding(value);
    setUserInformation(userInfo);
  };

  useEffect(() => {
    const isEmail = /^\S+@\S+\.\S+$/.test(userInformation);
    const isCrNumber = /^([0-9]{11})$/.test(userInformation);
    const isPhone =
      /^(?:\+?1[-\s]?)?(?:\(?([0-9]{3})\)?[-\s]?)?([0-9]{3})[-\s]?([0-9]{4})$/.test(
        userInformation,
      );

    if (userInformation === '') {
      setIsValid(undefined);
    }

    if (isEmail || isCrNumber || isPhone) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [userInformation]);

  useEffect(() => {
    if (query && query.length > 0) {
      setUserInformation(query);
    }
  }, [searchParams, query]);

  useEffect(() => {
    trackAdobeEvent({
      event: AdobeEvent.pageLoad,
      page: {
        name: 'password reset contact info entry',
      },
    });
  }, []);

  useEffect(() => {
    if (reason === PRResultCodes.INVALID_AUTH_TOKEN) {
      setWarning({
        label: 'Session expired. Please try again.',
        testID: 'information-entry-session-expired',
      });
    } else if (APICallFailure || reason === PRResultCodes.GENERIC_ERROR) {
      setWarning({
        label:
          'A system error occurred, but we’re working on it. Please try again.',
        testID: 'information-entry-system-error',
      });
    } else if (invalidInfoAPIResponse) {
      setWarning({
        label:
          'Please enter an email, mobile number, or Caesars Rewards Number.',
        testID: 'information-entry-invalid-error',
      });
    } else {
      setWarning(undefined);
    }
  }, [reason, APICallFailure, invalidInfoAPIResponse]);

  const formatPhoneNumber = (phone: string): string => {
    const hasDashChars = phone.length >= 6 && phone.includes('-');

    if (hasDashChars) {
      return phone;
    }

    const formattedPhoneNumber = `(${phone.slice(0, 3)}) ${phone.slice(
      3,
      6,
    )}-${phone.slice(6)}`;
    return formattedPhoneNumber;
  };

  const stripNonNumbers = (str: string) => {
    return str.replace(/[^a-zA-Z0-9]/g, '').replace(/\s/g, '');
  };

  const formatUserInformation = () => {
    if (/[a-zA-Z]/.test(userInformation)) {
      return userInformation;
    }

    return /^(\(\d{3}\)|\d{3})[- ]?\d{3}[- ]?\d{4}$/.test(userInformation)
      ? userInformation.replace(/\D/g, '')
      : userInformation;
  };

  return (
    <PRPageTemplate
      footerCTALabel="Submit"
      footerButtonDisabled={!isValid || loading}
      footerOnPress={submitInfo}
      loading={loading}
      testID="information-entry"
      warning={warning}
    >
      <View>
        <PRTitle testID="information-entry">Reset Your Password</PRTitle>
        <PRSubtitle>
          Enter your email, mobile, or Caesars Rewards Number.
        </PRSubtitle>
        <View mt={4}>
          <PRFormField
            onChangeText={handleUserInput}
            placeholder="Email, Mobile, or Caesars Rewards Number"
            dangerText={
              isValid === false && userInformation.length > 0
                ? [failedValidationCopy]
                : undefined
            }
            testID="information-entry"
            value={userInformation}
            accessibility={{
              accessible: true,
              accessibilityLabel:
                'Enter your email, mobile or Caesars Rewards number',
              accessibilityRole: 'text',
              accessibilityHint:
                'email address, mobile number, Caesars Rewards number',
              keyboardType: 'default',
            }}
          />
        </View>
      </View>
    </PRPageTemplate>
  );
};

export default PRInformationEntry;
